import { Controller } from '@hotwired/stimulus';
import * as pdfjsLib from 'pdfjs-dist';

// Renders a PDF (from its same-origin `src` value) as a vertical stack of
// canvases inside a scrollable container. Unlike <embed>/<iframe>, canvas
// rendering is reliable and scrollable on iOS Safari, which only displays the
// first page of an embedded PDF. Pages are rasterized at the container width so
// the contract stays readable on mobile, and re-rendered when that width
// changes.
//
// The `src` MUST be served same-origin (a Rails endpoint that streams the
// contract): pdf.js fetches it with XHR/Range requests, which are subject to
// CORS — a cross-origin S3 URL would be blocked and always fall back.
//
// When pdf.js cannot render (parse error, unexpected content type, network),
// the controller degrades gracefully IN PAGE — it never opens a new tab and
// never ejects the user from the signature flow:
//   1. pdf.js canvas (scrollable, works on iOS)
//   2. native <iframe> render of the same-origin URL (works on desktop/Android,
//      stays in page on iOS)
//   3. inline download link as a last resort
const TRANSLATIONS = {
  loading: {
    fr: 'Chargement du contrat…',
    en: 'Loading the contract…',
    es: 'Cargando el contrato…',
  },
  download: {
    fr: 'Télécharger le contrat',
    en: 'Download the contract',
    es: 'Descargar el contrato',
  },
  frameTitle: {
    fr: 'Contrat de souscription',
    en: 'Subscription contract',
    es: 'Contrato de suscripción',
  },
};

const RESIZE_DEBOUNCE_MS = 150;

export default class PdfViewerController extends Controller {
  static values = {src: String}

  connect() {
    this.locale = window.getCurrentLocale?.() || 'fr';
    this.renderTasks = [];
    this.lastRenderedWidth = 0;
    this.resizeTimeout = null;

    this.#configureWorker();
    this.#showLoading();
    this.#loadDocument();

    this.resizeObserver = new ResizeObserver(() => this.#scheduleRender());
    this.resizeObserver.observe(this.element);
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    clearTimeout(this.resizeTimeout);
    this.#cancelRenderTasks();
    this.pdfDocument?.destroy();
  }

  #configureWorker() {
    const workerSrc = document.querySelector('meta[name="pdf-worker-src"]')?.content;

    if (workerSrc) pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
  }

  #loadDocument() {
    pdfjsLib.getDocument(this.srcValue).promise
      .then((pdfDocument) => {
        this.pdfDocument = pdfDocument;
        this.#renderAllPages();
      })
      .catch((error) => {
        // Surface the real cause (CORS / parse / network) instead of swallowing
        // it, then degrade in page rather than ejecting the user.
        console.error('[pdf-viewer] could not render the contract with pdf.js', error);
        this.#showInlineFallback();
      });
  }

  #scheduleRender() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.#renderAllPages(), RESIZE_DEBOUNCE_MS);
  }

  #renderAllPages() {
    if (!this.pdfDocument) return;

    const width = this.element.clientWidth;
    // Skip while hidden (d-none → width 0) or when the width has not changed
    // (appending canvases grows the height and re-triggers the observer).
    if (width === 0 || width === this.lastRenderedWidth) return;

    this.lastRenderedWidth = width;
    this.#cancelRenderTasks();
    this.#clear();

    const devicePixelRatio = window.devicePixelRatio || 1;
    const pageNumbers = Array.from({length: this.pdfDocument.numPages}, (_, index) => index + 1);

    pageNumbers.forEach((pageNumber) => {
      // Append the canvas synchronously so pages keep their order even though
      // getPage resolves asynchronously.
      const canvas = document.createElement('canvas');
      canvas.className = 'pdf-viewer-page';
      this.element.append(canvas);

      this.pdfDocument.getPage(pageNumber).then((page) => {
        const baseViewport = page.getViewport({scale: 1});
        const viewport = page.getViewport({scale: width / baseViewport.width});

        canvas.width = Math.floor(viewport.width * devicePixelRatio);
        canvas.height = Math.floor(viewport.height * devicePixelRatio);

        const renderTask = page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
          transform: devicePixelRatio === 1 ? null : [devicePixelRatio, 0, 0, devicePixelRatio, 0, 0],
        });
        this.renderTasks.push(renderTask);
      });
    });
  }

  #cancelRenderTasks() {
    this.renderTasks.forEach((renderTask) => renderTask.cancel());
    this.renderTasks = [];
  }

  #clear() {
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }
  }

  #showLoading() {
    this.#clear();

    const paragraph = document.createElement('p');
    paragraph.className = 'pdf-viewer-message';
    paragraph.textContent = TRANSLATIONS.loading[this.locale];
    this.element.append(paragraph);
  }

  // In-page degradation: render the same-origin URL natively in an <iframe>
  // (the browser's built-in PDF viewer), with an inline download link beneath
  // it for the rare case where the native viewer cannot display it either.
  #showInlineFallback() {
    this.#clear();
    this.resizeObserver?.disconnect();

    const frame = document.createElement('iframe');
    frame.className = 'pdf-viewer-frame';
    frame.src = this.srcValue;
    frame.title = TRANSLATIONS.frameTitle[this.locale];
    this.element.append(frame);

    const link = document.createElement('a');
    link.className = 'pdf-viewer-download';
    link.href = this.srcValue;
    link.download = '';
    link.textContent = TRANSLATIONS.download[this.locale];
    this.element.append(link);
  }
}
